import React, { useState } from 'react';
import { X, Lock, Search, Calendar as CalendarIcon } from 'lucide-react';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import services from '../data/services';
import { useAlert } from '../hooks/UseAlert.js';
import AlertModal from './AlertModal.js';

const BlockSlotModal = ({ selectedDate, onClose, onSuccess, isRestrictedAdmin }) => {
  const [blockData, setBlockData] = useState({
    startTime: '',
    duration: '1h00',
    reason: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    sessionType: '',
    selectedMassage: null,
    blockType: 'single', // 'single' ou 'multi'
    endDate: '', // Pour les blocages multi-jours
    customDuration: '' // Pour les durées personnalisées
  });
  const [loading, setLoading] = useState(false);
  const [massageSearch, setMassageSearch] = useState('');

  const BOOKING_START_DATE = new Date('2026-02-09T00:00:00');

  const { alertConfig, closeAlert, warning, error, success } = useAlert();

  // Horaires d'ouverture du salon
  const openingHours = {
    1: null, // Lundi fermé
    2: { start: "14:00", end: "20:00" }, // Mardi
    3: { start: "11:00", end: "20:00" }, // Mercredi
    4: { start: "11:00", end: "20:00" }, // Jeudi
    5: { start: "11:00", end: "20:00" }, // Vendredi
    6: { start: "10:00", end: "18:00" }, // Samedi
    0: null, // Dimanche fermé
  };

  // Générer les créneaux disponibles en fonction du jour sélectionné
  const getAvailableTimeSlots = () => {
    const dayOfWeek = selectedDate.getDay();
    const hours = openingHours[dayOfWeek];

    if (!hours) {
      return []; // Salon fermé
    }

    const slots = [];
    const [startH, startM] = hours.start.split(':').map(Number);
    const [endH, endM] = hours.end.split(':').map(Number);

    let currentMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    while (currentMinutes <= endMinutes) {
      const h = Math.floor(currentMinutes / 60);
      const m = currentMinutes % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      currentMinutes += 15;
    }

    return slots;
  };

  const timeSlots = getAvailableTimeSlots();

  // Construire la liste complète des massages depuis services.js
  const buildMassagesList = () => {
    const massages = [];

    Object.entries(services).forEach(([categoryKey, categoryData]) => {
      const isObjectCategory = categoryData && typeof categoryData === 'object' && !Array.isArray(categoryData);
      const categoryTitle = categoryKey;
      const serviceList = isObjectCategory ? (categoryData.items || []) : (Array.isArray(categoryData) ? categoryData : []);

      serviceList.forEach(service => {
        // Gérer les durées variables (ex: "1h00-1h30")
        if (service.duration && service.duration.includes('-')) {
          const durations = service.duration.split('-');
          durations.forEach(dur => {
            massages.push({
              name: `${service.name} (${dur.trim()})`,
              duration: dur.trim(),
              category: categoryTitle
            });
          });
        } else {
          massages.push({
            name: service.name,
            duration: service.duration,
            category: categoryTitle
          });
        }
      });
    });

    return massages;
  };

  const allMassages = buildMassagesList();

  // Filtrer les massages selon la recherche
  const filteredMassages = massageSearch
    ? allMassages.filter(m =>
      m.name.toLowerCase().includes(massageSearch.toLowerCase()) ||
      m.category.toLowerCase().includes(massageSearch.toLowerCase())
    )
    : allMassages;

  // Durées prédéfinies
  const durations = [
    { value: '15mins', label: '15 minutes' },
    { value: '30mins', label: '30 minutes' },
    { value: '45mins', label: '45 minutes' },
    { value: '1h00', label: '1h' },
    { value: '1h15', label: '1h15' },
    { value: '1h30', label: '1h30' },
    { value: '1h45', label: '1h45' },
    { value: '2h00', label: '2h' },
    { value: 'custom', label: 'Durée personnalisée...' }
  ];

  // Durées pour blocage multi-jours
  const multiDayDurations = [
    { value: '2', label: '2 jours' },
    { value: '3', label: '3 jours' },
    { value: '4', label: '4 jours' },
    { value: '5', label: '5 jours' },
    { value: '6', label: '6 jours' },
    { value: '7', label: '1 semaine' },
    { value: 'custom', label: 'Période personnalisée...' }
  ];

  // Toutes les raisons disponibles pour tous les admins
  const reasons = [
    { value: "Séance forfait", label: "Séance forfait" },
    { value: "Séance massage", label: "Séance massage" },
    { value: "Pause personnelle", label: "Pause personnelle" },
    { value: "Formation", label: "Formation" },
    { value: "Congés", label: "Congés" },
    { value: "Autre", label: "Autre" }
  ];

  const calculateBlockedSlots = (startTime, duration) => {
    const slots = [];
    const [hours, minutes] = startTime.split(':').map(Number);
    let totalMinutes = hours * 60 + minutes;

    let durationMinutes = 0;
    if (duration.includes('h')) {
      const parts = duration.match(/(\d+)h(\d+)?/);
      if (parts) {
        durationMinutes = parseInt(parts[1]) * 60 + (parts[2] ? parseInt(parts[2]) : 0);
      }
    } else if (duration.includes('mins')) {
      durationMinutes = parseInt(duration);
    }

    // Ajouter 15 minutes de nettoyage
    const totalDuration = durationMinutes + 15;
    const slotsNeeded = Math.ceil(totalDuration / 15);

    for (let i = 0; i < slotsNeeded; i++) {
      const slotMinutes = totalMinutes + (i * 15);
      const h = Math.floor(slotMinutes / 60);
      const m = slotMinutes % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }

    return slots;
  };

  const handleMassageSelect = (massage) => {
    setBlockData({
      ...blockData,
      selectedMassage: massage,
      duration: massage.duration,
      reason: blockData.reason || 'Séance massage'
    });
    setMassageSearch('');
  };

  // Vérifier si une date est avant la date limite de réservation
  const isDateBeforeBookingStart = (date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const limitDate = new Date(BOOKING_START_DATE);
    limitDate.setHours(0, 0, 0, 0);
    return checkDate < limitDate;
  };

  // Générer la liste des dates à bloquer pour un blocage multi-jours
  const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(current.toLocaleDateString('fr-CA'));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  const handleSubmit = async () => {
    if (!blockData.reason) {
      error('Veuillez sélectionner une raison de blocage');
      return;
    }

    // Validation pour les séances client (pour tous les admins)
    if (blockData.reason === 'Séance forfait' || blockData.reason === 'Séance massage') {
      if (!blockData.clientName) {
        error('Veuillez indiquer le nom du client');
        return;
      }
      if (!blockData.selectedMassage) {
        error('Veuillez sélectionner un massage');
        return;
      }
      if (!blockData.startTime) {
        error('Veuillez sélectionner une heure de début');
        return;
      }
      // ✨ Validation email (optionnelle mais format vérifié si rempli)
      if (blockData.clientEmail && !blockData.clientEmail.includes('@')) {
        error('Format d\'email invalide');
        return;
      }
      // ✨ Validation téléphone (optionnelle mais format vérifié si rempli)
      if (blockData.clientPhone && !/^[\d\s\+\-\(\)]+$/.test(blockData.clientPhone)) {
        error('Format de téléphone invalide (chiffres, espaces, +, -, (, ) autorisés)');
        return;
      }
    }

    // Validation pour blocage multi-jours
    if (blockData.blockType === 'multi') {
      if (!blockData.endDate) {
        error('Veuillez sélectionner une date de fin');
        return;
      }
      if (new Date(blockData.endDate) < new Date(selectedDate)) {
        error('La date de fin doit être après la date de début');
        return;
      }
    } else {
      // Validation pour blocage single
      if (!blockData.startTime) {
        error('Veuillez sélectionner une heure de début');
        return;
      }
    }

    setLoading(true);

    try {
      if (blockData.blockType === 'multi') {
        // Blocage multi-jours (congés, formation, etc.)
        const datesToBlock = generateDateRange(selectedDate, blockData.endDate);

        for (const dateStr of datesToBlock) {
          // Vérifier les horaires d'ouverture pour chaque jour
          const dayOfWeek = new Date(dateStr).getDay();
          const hours = openingHours[dayOfWeek];

          if (!hours) continue; // Ignorer les jours fermés

          // Bloquer toute la journée
          const allSlots = [];
          const [startH, startM] = hours.start.split(':').map(Number);
          const [endH, endM] = hours.end.split(':').map(Number);

          let currentMinutes = startH * 60 + startM;
          const endMinutes = endH * 60 + endM;

          while (currentMinutes <= endMinutes) {
            const h = Math.floor(currentMinutes / 60);
            const m = currentMinutes % 60;
            allSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
            currentMinutes += 15;
          }

          // Créer un blocage pour toute la journée
          await addDoc(collection(db, 'bookings'), {
            userId: 'admin',
            userName: 'Admin - Blocage',
            userEmail: 'alaia.cristalbienetre@gmail.com',
            userPhone: '',
            serviceName: blockData.reason,
            serviceDuration: 'Journée complète',
            servicePrice: 'Bloqué',
            date: dateStr,
            timeSlot: hours.start,
            blockedSlots: allSlots,
            createdAt: new Date().toISOString(),
            status: 'blocked',
            isAdminBlock: true,
            blockType: 'multi',
            multiBlockInfo: {
              startDate: selectedDate.toLocaleDateString('fr-CA'),
              endDate: blockData.endDate,
              totalDays: datesToBlock.length
            }
          });
        }

        success(`Période bloquée avec succès !\n\n${datesToBlock.length} jour(s) bloqué(s)\nDu ${new Date(selectedDate).toLocaleDateString('fr-FR')} au ${new Date(blockData.endDate).toLocaleDateString('fr-FR')}\n\nMotif : ${blockData.reason}`);

      } else {
        // Blocage single (une séance)
        const newBlockedSlots = calculateBlockedSlots(blockData.startTime, blockData.duration);
        const dateStr = selectedDate.toLocaleDateString('fr-CA');

        // Déterminer le nom du service
        let serviceName = blockData.reason;
        if (blockData.selectedMassage) {
          serviceName = blockData.selectedMassage.name;
        }

        // Créer un blocage admin dans Firebase
        await addDoc(collection(db, 'bookings'), {
          userId: 'admin',
          userName: 'Admin - Blocage',
          userEmail: 'alaia.cristalbienetre@gmail.com',
          userPhone: '',
          serviceName: serviceName,
          serviceDuration: blockData.duration,
          servicePrice: 'Bloqué',
          date: dateStr,
          timeSlot: blockData.startTime,
          blockedSlots: newBlockedSlots,
          createdAt: new Date().toISOString(),
          status: 'blocked',
          clientName: blockData.clientName,
          clientEmail: blockData.clientEmail || '',
          clientPhone: blockData.clientPhone || '',
          sessionType: blockData.reason,
          isAdminBlock: true,
          blockType: 'single'
        });

        let successMessage = `Créneau bloqué avec succès !\n\n${serviceName}\n📅 ${new Date(dateStr).toLocaleDateString('fr-FR')}\n🕐 ${blockData.startTime}\n⏱️ Durée : ${blockData.duration}`;

        if (blockData.clientName) {
          successMessage += `\n\n👤 Client : ${blockData.clientName}`;
          if (blockData.clientEmail) {
            successMessage += `\n📧 Email : ${blockData.clientEmail}`;
          }
          if (blockData.clientPhone) {
            successMessage += `\n📞 Téléphone : ${blockData.clientPhone}`;
          }
        } else {
          successMessage += `\n\nMotif : ${blockData.reason}`;
        }

        success(successMessage);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur blocage:', error);
      error('Erreur lors du blocage du créneau');
    } finally {
      setLoading(false);
    }
  };

  const showMassageSelection = blockData.reason === 'Séance forfait' || blockData.reason === 'Séance massage';

  // Calculer la date de fin automatiquement si une durée est sélectionnée
  const handleMultiDayDurationChange = (value) => {
    if (value === 'custom') {
      setBlockData({ ...blockData, duration: value, endDate: '' });
    } else {
      const days = parseInt(value);
      const endDate = new Date(selectedDate);
      endDate.setDate(endDate.getDate() + days - 1); // -1 car on inclut le premier jour
      setBlockData({
        ...blockData,
        duration: value,
        endDate: endDate.toLocaleDateString('fr-CA')
      });
    }
  };

  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl z-10">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-light text-purple-900 flex items-center gap-2">
                  <Lock className="w-6 h-6" />
                  Bloquer un créneau
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedDate.toLocaleDateString('fr-FR', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Type de blocage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de blocage *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setBlockData({
                    ...blockData,
                    blockType: 'single',
                    endDate: '',
                    duration: '1h00'
                  })}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${blockData.blockType === 'single'
                      ? 'border-purple-500 bg-purple-50 text-purple-900 font-medium'
                      : 'border-gray-300 hover:border-gray-400'
                    }`}
                >
                  <CalendarIcon className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Bloquer un créneau</span>
                </button>
                <button
                  onClick={() => setBlockData({
                    ...blockData,
                    blockType: 'multi',
                    startTime: '',
                    selectedMassage: null,
                    clientName: '',
                    clientEmail: '', // ✨ Reset aussi les nouveaux champs
                    clientPhone: '' // ✨ Reset aussi les nouveaux champs
                  })}
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${blockData.blockType === 'multi'
                      ? 'border-purple-500 bg-purple-50 text-purple-900 font-medium'
                      : 'border-gray-300 hover:border-gray-400'
                    }`}
                >
                  <CalendarIcon className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm">Bloquer une période</span>
                </button>
              </div>
            </div>

            {/* Durée/Période pour blocage multi-jours */}
            {blockData.blockType === 'multi' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée de la période *
                </label>
                {blockData.duration === 'custom' ? (
                  <div className="space-y-2">
                    <input
                      type="date"
                      value={blockData.endDate}
                      onChange={(e) => setBlockData({ ...blockData, endDate: e.target.value })}
                      min={selectedDate.toLocaleDateString('fr-CA')}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                    <button
                      onClick={() => setBlockData({ ...blockData, duration: '2', endDate: '' })}
                      className="text-sm text-purple-600 hover:text-purple-700"
                    >
                      ← Revenir aux durées prédéfinies
                    </button>
                  </div>
                ) : (
                  <select
                    value={blockData.duration}
                    onChange={(e) => handleMultiDayDurationChange(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  >
                    <option value="">Sélectionner une durée</option>
                    {multiDayDurations.map((dur) => (
                      <option key={dur.value} value={dur.value}>
                        {dur.label}
                      </option>
                    ))}
                  </select>
                )}

                {blockData.endDate && (
                  <div className="mt-2 p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-900">
                      <strong>Période sélectionnée :</strong><br />
                      Du {new Date(selectedDate).toLocaleDateString('fr-FR')} au {new Date(blockData.endDate).toLocaleDateString('fr-FR')}
                      <br />
                      <span className="text-purple-700">
                        {Math.ceil((new Date(blockData.endDate) - new Date(selectedDate)) / (1000 * 60 * 60 * 24)) + 1} jour(s) bloqué(s)
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Heure de début (uniquement pour blocage single) */}
            {blockData.blockType === 'single' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Heure de début *
                </label>
                <select
                  value={blockData.startTime}
                  onChange={(e) => setBlockData({ ...blockData, startTime: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  disabled={timeSlots.length === 0}
                >
                  <option value="">
                    {timeSlots.length === 0 ? 'Salon fermé ce jour' : 'Choisir un créneau'}
                  </option>
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Raison du blocage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raison du blocage *
              </label>
              <select
                value={blockData.reason}
                onChange={(e) => {
                  setBlockData({
                    ...blockData,
                    reason: e.target.value,
                    selectedMassage: null,
                    clientName: '',
                    clientEmail: '', // ✨ Reset email
                    clientPhone: ''  // ✨ Reset téléphone
                  });
                }}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
              >
                <option value="">Sélectionner une raison</option>
                {reasons.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélection du massage pour les réservations client */}
            {showMassageSelection && blockData.blockType === 'single' && (
              <>
                {/* Nom du client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du client *
                  </label>
                  <input
                    type="text"
                    value={blockData.clientName}
                    onChange={(e) => setBlockData({ ...blockData, clientName: e.target.value })}
                    placeholder="Ex: Marie Dupont"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  />
                </div>

                {/* ✨ NOUVEAU: Email du client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email du client
                    <span className="text-gray-500 text-xs ml-2">(optionnel)</span>
                  </label>
                  <input
                    type="email"
                    value={blockData.clientEmail}
                    onChange={(e) => setBlockData({ ...blockData, clientEmail: e.target.value })}
                    placeholder="Ex: marie.dupont@example.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  />
                </div>

                {/* ✨ NOUVEAU: Téléphone du client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone du client
                    <span className="text-gray-500 text-xs ml-2">(optionnel)</span>
                  </label>
                  <input
                    type="tel"
                    value={blockData.clientPhone}
                    onChange={(e) => setBlockData({ ...blockData, clientPhone: e.target.value })}
                    placeholder="Ex: 06 12 34 56 78"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  />
                </div>

                {/* Sélection du massage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Massage sélectionné *
                  </label>

                  {blockData.selectedMassage ? (
                    <div className="bg-purple-50 border-2 border-purple-500 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-purple-900">{blockData.selectedMassage.name}</p>
                          <p className="text-sm text-purple-700">{blockData.selectedMassage.category}</p>
                          <p className="text-sm text-gray-600 mt-1">
                            Durée : {blockData.selectedMassage.duration} + 15min nettoyage
                          </p>
                        </div>
                        <button
                          onClick={() => setBlockData({ ...blockData, selectedMassage: null })}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={massageSearch}
                          onChange={(e) => setMassageSearch(e.target.value)}
                          placeholder="Rechercher un massage..."
                          className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                        />
                      </div>

                      <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg">
                        {filteredMassages.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            Aucun massage trouvé
                          </div>
                        ) : (
                          filteredMassages.map((massage, index) => (
                            <button
                              key={index}
                              onClick={() => handleMassageSelect(massage)}
                              className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                            >
                              <p className="font-medium text-gray-900">{massage.name}</p>
                              <div className="flex justify-between items-center mt-1">
                                <p className="text-sm text-gray-600">{massage.category}</p>
                                <p className="text-sm text-purple-600 font-medium">{massage.duration}</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Durée manuelle uniquement pour les raisons non-client et blocage single */}
            {!showMassageSelection && blockData.blockType === 'single' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée
                </label>
                {blockData.duration === 'custom' ? (
                  <input
                    type="text"
                    value={blockData.customDuration}
                    onChange={(e) => setBlockData({ ...blockData, customDuration: e.target.value })}
                    placeholder="Ex: 2h30, 3h00..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  />
                ) : (
                  <select
                    value={blockData.duration}
                    onChange={(e) => setBlockData({ ...blockData, duration: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:outline-none"
                  >
                    {durations.map((dur) => (
                      <option key={dur.value} value={dur.value}>
                        {dur.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Info sur le blocage */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mt-4">
              <p className="text-xs text-amber-800">
                {blockData.blockType === 'multi' ? (
                  <>
                    <strong>ℹ️ Blocage de période :</strong> Tous les créneaux de chaque jour seront bloqués pendant la période sélectionnée.
                    Ces jours apparaîtront grisés dans le calendrier.
                  </>
                ) : (
                  <>
                    <strong>ℹ️ Blocage de créneau :</strong> Ce créneau sera bloqué pour les clients et ne pourra pas être réservé en ligne.
                    {blockData.selectedMassage && (
                      <span className="block mt-1">
                        Durée totale bloquée : {blockData.selectedMassage.duration} + 15min de nettoyage
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              {loading ? 'Blocage en cours...' : blockData.blockType === 'multi' ? 'Bloquer la période' : 'Bloquer le créneau'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockSlotModal;